import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../index";
import {
  selectors as shipBuilderSelectors,
  selectMinimumCrew,
} from "./shipBuilderSelectors";
import { calculatePowerPlantFuelConsumption } from "../../utils/calculations/fuelCalculations";
import { DriveType, Drive } from "../../types/drive";
import { CrewRole, CREW_TYPES } from "../../types/crew";
import { CargoSystem, PowerSystem } from "../../types/optionalSystems";
import { calculateDeepPenetrationScannersSpecs } from "../../utils/calculations/optionalSystems";
import { calculateExtensionNetSpecs } from "../../utils/calculations/optionalSystems";
import { calculateDistributedArraySpecs } from "../../utils/calculations/optionalSystems";
import { calculateExtendedArraySpecs } from "../../utils/calculations/optionalSystems";
import { calculateRapidDeploymentExtendedArraySpecs } from "../../utils/calculations/optionalSystems";
import {
  calculateDetachableBridgeStats,
  calculateHolographicControlsStats,
  calculateSensorStationStats,
} from "../../utils/calculations/optionalSystems";
import { calculateBridgeStats } from "../../utils/calculations/bridgeCalculations";

import {
  calculateMiningDroneSpecs,
  calculateProbeDroneSpecs,
  calculateAdvancedProbeDroneSpecs,
  calculateRepairDroneSpecs,
} from "../../utils/calculations/optionalSystems";
import { getDriveTons } from "../../constants/drives";

// Type definitions will be inferred from the actual selectors

// Fuel cost selectors
export const selectFullRefuelCostRefined = createSelector(
  [(state: RootState) => selectTotalUsableFuel(state)],
  (totalUsableFuel: number): number => totalUsableFuel * 500,
);

export const selectFullRefuelCostUnrefined = createSelector(
  [(state: RootState) => selectTotalUsableFuel(state)],
  (totalUsableFuel: number): number => totalUsableFuel * 100,
);

// Fuel requirement selectors
export const selectPowerPlantFuelRequirement = createSelector(
  [
    (state: RootState) => state.shipBuilder.powerPlant,
    (state: RootState) => state.shipBuilder.powerPlantReserveHours,
  ],
  (powerPlant, reserveHours): number => {
    if (!powerPlant || !reserveHours) return 0;
    // Allow fractional weeks for proportional fuel calculation
    const weeks = reserveHours / 168;
    return calculatePowerPlantFuelConsumption(powerPlant, weeks);
  },
);

export const selectReactionDriveFuelRequirement = createSelector(
  [
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => state.shipBuilder.reactionDriveHours || 1,
    (state: RootState) => selectTotalExternalSmallCraftTonnage(state),
  ],
  (drives, shipSize, hours, externalSmallCraftTonnage): number => {
    const thrust = drives.find((d) => d.type === "reaction")?.rating || 0;
    // Include external small craft tonnage in fuel calculations as per Traveller rules
    const effectiveTonnage = shipSize + externalSmallCraftTonnage;
    return effectiveTonnage * 0.025 * thrust * hours || 0;
  },
);

export const selectJumpDriveFuelRequirement = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => state.shipBuilder.jumpDriveReserveParsecs,
    (state: RootState) => state.shipBuilder.drives,
  ],
  (shipSize, parsecs, drives): number => {
    const jumpDrive = drives.find((d) => d.type === "jump");
    
    // If no jump drive is installed, no fuel is required
    if (!jumpDrive) {
      return 0;
    }
    
    const jumpRating = jumpDrive.rating;
    // Use the same calculation as the UI: shipSize * 0.1 * jumpRating * numberOfJumps
    // This matches exactly what's displayed in the Jump Drive Reserve section
    const fuelPerJump = shipSize * 0.1 * jumpRating;
    const numberOfJumps = parsecs || 1; // Use parsecs if set, otherwise default to 1 jump
    return fuelPerJump * numberOfJumps || 0;
  },
);

export const selectTotalFuelRequirement = createSelector(
  [
    (state: RootState) => selectPowerPlantFuelRequirement(state),
    (state: RootState) => selectReactionDriveFuelRequirement(state),
    (state: RootState) => selectJumpDriveFuelRequirement(state),
  ],
  (powerPlantFuel, reactionFuel, jumpFuel): number =>
    powerPlantFuel + reactionFuel + jumpFuel,
);

// Fuel tank capacity selectors
export const selectOptionalFuelSystemsCapacity = createSelector(
  [(state: RootState) => state.optionalSystems.fuel || []],
  (fuelSystems): number => {
    const fuelCargoContainerCapacity = (fuelSystems || [])
      .filter(
        (s) =>
          s.subtype === "fuelCargoContainer" &&
          s.properties?.allocation === "fuel",
      )
      .reduce((sum, s) => sum + (s.properties?.capacity || 0), 0);

    const collapsibleTanksCapacity = (fuelSystems || [])
      .filter((s) => s.subtype === "collapsibleTank")
      .reduce((sum, s) => sum + (s.properties?.fullTonnage || 0), 0);

    const dropTanksCapacity = (fuelSystems || [])
      .filter((s) => s.subtype === "dropTank")
      .reduce((sum, s) => sum + (s.properties?.dropTankTonnage || 0), 0);

    const mountableTankCapacity = (fuelSystems || [])
      .filter((s) => s.subtype === "mountableTank")
      .reduce((sum, s) => sum + (s.tonnage || 0), 0);

    return (
      fuelCargoContainerCapacity +
      collapsibleTanksCapacity +
      dropTanksCapacity +
      mountableTankCapacity
    );
  },
);

export const selectFuelCompartmentReduction = createSelector(
  [(state: RootState) => state.optionalSystems.fuel || []],
  (fuelSystems): number => {
    const fuelTankCompartments = (fuelSystems || []).filter(
      (s) => s.subtype === "fuelTankCompartment",
    );
    return fuelTankCompartments.reduce(
      (sum, comp) => sum + comp.cost / 4000,
      0,
    );
  },
);

export const selectHasMetalHydrideStorage = createSelector(
  [(state: RootState) => state.optionalSystems.fuel || []],
  (fuelSystems): boolean =>
    fuelSystems.some((s) => s.subtype === "metalHydride"),
);

export const selectTotalPhysicalTankage = createSelector(
  [
    (state: RootState) => state.shipBuilder.fuelTanks || 0,
    (state: RootState) => selectOptionalFuelSystemsCapacity(state),
  ],
  (fuelTanks, optionalCapacity): number => fuelTanks + optionalCapacity,
);

export const selectTotalUsableFuel = createSelector(
  [
    (state: RootState) => selectTotalPhysicalTankage(state),
    (state: RootState) => selectFuelCompartmentReduction(state),
    (state: RootState) => selectHasMetalHydrideStorage(state),
  ],
  (totalTankage, compartmentReduction, hasMetalHydride): number => {
    let totalAfterReductions = totalTankage - compartmentReduction;
    if (hasMetalHydride) {
      totalAfterReductions = totalAfterReductions / 2;
    }
    return Math.max(0, totalAfterReductions);
  },
);

export const selectMaxAllocatableFuel = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => state.shipBuilder.shipStats?.totalWeight || 0,
    (state: RootState) => selectTotalPhysicalTankage(state),
  ],
  (shipSize, totalWeight, totalTankage): number => {
    return Math.max(0, Math.floor(shipSize - (totalWeight - totalTankage)));
  },
);

// Combat thrust selectors
export const selectCombatThrustInfo = createSelector(
  [
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.shipBuilder.reactionDriveHours || 1,
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => selectTotalExternalSmallCraftTonnage(state),
  ],
  (drives, hours, shipSize, externalSmallCraftTonnage) => {
    const reactionDrives = drives.filter((d) => d.type === "reaction");
    if (reactionDrives.length === 0) return null;

    const maxThrust = Math.max(...reactionDrives.map((d) => d.rating));
    const totalHours = hours * 168; // 168 hours per week
    
    // Calculate effective thrust based on total tonnage including external small craft
    const effectiveTonnage = shipSize + externalSmallCraftTonnage;
    const effectiveThrust = Math.floor(maxThrust * (shipSize / effectiveTonnage));
    const totalThrust = effectiveThrust * totalHours * 10; // 10 combat rounds per hour

    return {
      maxThrust,
      effectiveThrust,
      hours: totalHours,
      totalThrust,
      roundsPerHour: 10,
      effectiveTonnage,
      externalSmallCraftTonnage,
    };
  },
);

// Fuel validation selectors
export const selectFuelValidationErrors = createSelector(
  [
    (state: RootState) => selectTotalFuelRequirement(state),
    (state: RootState) => state.shipBuilder.fuelTanks || 0,
    (state: RootState) => state.optionalSystems.fuel || [],
  ],
  (totalRequired, fuelTanks, fuelSystems): string[] => {
    const errors: string[] = [];

    // Check if total usable fuel meets requirements
    const totalUsableFuel = selectTotalUsableFuel({
      shipBuilder: { fuelTanks },
      optionalSystems: { fuel: fuelSystems },
    } as RootState);

    if (totalUsableFuel < totalRequired) {
      errors.push(
        `Insufficient fuel tankage. Required: ${totalRequired} tons, Available: ${totalUsableFuel.toFixed(1)} tons`,
      );
    }

    return errors;
  },
);

// Mortgage selector (per Traveller rules: total cost / 240 for 40-year repayment)
export const selectMortgage = createSelector(
  [(state: RootState) => shipBuilderSelectors.selectShipStats(state)?.totalCost || 0],
  (totalCost: number): number => totalCost / 240,
);

// Weapon and defensive system summary selectors
export const selectWeaponAndDefensiveTotals = createSelector(
  [
    (state: RootState) => state.shipBuilder.weaponMounts || [],
    (state: RootState) => state.shipBuilder.defensiveSystems || [],
  ],
  (weaponMounts, defensiveSystems) => {
    if (!weaponMounts || !Array.isArray(weaponMounts)) {
      weaponMounts = [];
    }
    if (!defensiveSystems || !Array.isArray(defensiveSystems)) {
      defensiveSystems = [];
    }
    
    const totals = weaponMounts.reduce(
      (acc, mount) => {
        acc.totalCost += mount.cost;
        // Use mount.tons which includes mount base weight + weapon weight per Traveller rules
        acc.totalTons += mount.tons || 0;
        acc.totalPower += mount.power;
        acc.totalWeapons += mount.weapons.length;
        acc.totalHardpoints += mount.hardpointsUsed;
        acc.totalGunners += 1; // Each mount requires 1 gunner
        return acc;
      },
      {
        totalCost: 0,
        totalTons: 0,
        totalPower: 0,
        totalWeapons: 0,
        totalHardpoints: 0,
        totalGunners: 0,
      },
    );
    const defensiveTotals = defensiveSystems.reduce(
      (acc, system) => {
        // Multiply by quantity per Traveller rules
        const quantity = system.quantity || 1;
        acc.totalCost += (system.cost || 0) * quantity;
        acc.totalTons += (system.weight || 0) * quantity;
        acc.totalPower += (system.power || 0) * quantity;
        acc.totalCrew += (system.crew || 0) * quantity;
        return acc;
      },
      {
        totalCost: 0,
        totalTons: 0,
        totalPower: 0,
        totalCrew: 0,
      },
    );
    return {
      totalCost: totals.totalCost + defensiveTotals.totalCost,
      totalTons: totals.totalTons + defensiveTotals.totalTons,
      totalPower: totals.totalPower + defensiveTotals.totalPower,
      totalWeapons: totals.totalWeapons,
      totalHardpoints: totals.totalHardpoints,
      totalGunners: totals.totalGunners,
      totalDefensiveCrew: defensiveTotals.totalCrew,
    };
  },
);

export const selectWeaponTypeDistribution = createSelector(
  [(state: RootState) => state.shipBuilder.weaponMounts || []],
  (weaponMounts) => {
    if (!weaponMounts || !Array.isArray(weaponMounts)) {
      return {};
    }
    return weaponMounts.reduce(
      (types, mount) => {
        mount.weapons.forEach((weapon) => {
          const name = weapon.name;
          if (!types[name]) types[name] = 0;
          types[name]++;
        });
        return types;
      },
      {} as Record<string, number>,
    );
  },
);

export const selectMountTypeDistribution = createSelector(
  [(state: RootState) => state.shipBuilder.weaponMounts || []],
  (weaponMounts) => {
    if (!weaponMounts || !Array.isArray(weaponMounts)) {
      return {};
    }
    return weaponMounts.reduce(
      (types, mount) => {
        const type = mount.turretType
          ? `${mount.turretType} turret`
          : mount.type;
        if (!types[type]) types[type] = 0;
        types[type]++;
        return types;
      },
      {} as Record<string, number>,
    );
  },
);

export const selectTotalDrivePower = createSelector(
  [
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.shipBuilder.shipSize,
  ],
  (drives, shipSize) => {
    if (!drives || !Array.isArray(drives) || !shipSize) {
      return 0;
    }
    // Only calculate power for primary drives - additional drives don't consume power
    const primaryDrives = drives.filter(drive => drive.role === 'primary');
    
    return primaryDrives.reduce((total, drive) => {
      if (drive.rating >= 0) {
        // Use correct Traveller rules: Power = 10% of hull × drive rating
        switch (drive.type) {
          case DriveType.MANEUVER:
            // Maneuver Drive: 10% of hull × thrust rating
            return total + (shipSize * 0.1 * drive.rating);
          case DriveType.JUMP:
            // Jump Drive: 10% of hull × jump rating
            return total + (shipSize * 0.1 * drive.rating);
          case DriveType.REACTION:
            // Reaction Drive: 0 Power (no power required)
            return total + 0;
          default:
            return total;
        }
      }
      return total;
    }, 0);
  },
);

// Stateroom and passenger summary selectors
export const selectAssignedCrewCount = createSelector(
  [selectMinimumCrew, (state: RootState) => state.shipBuilder.crewOverrides],
  (minimumCrew, crewOverrides) => {
    if (!minimumCrew || typeof minimumCrew !== 'object') {
      return 0;
    }
    return Object.entries(minimumCrew).reduce(
      (total, [roleKey, minimumCount]) => {
        // Convert the string key back to the enum value
        const role = Object.values(CrewRole).find(r => r === roleKey) as CrewRole;
        if (!role) return total; // Skip if we can't find the role
        
        // Ensure crewOverrides is an object and has the role
        const overrides = crewOverrides || {};
        const assigned =
          overrides[role] !== undefined
            ? overrides[role]
            : minimumCount || 0;
        return (
          total +
          (typeof assigned === "number" && !isNaN(assigned) ? assigned : 0)
        );
      },
      0,
    );
  },
);

// Life support cost selector (per Traveller rules: Cr1000 per stateroom, Cr1000 per person, Cr100 per low berth)
export const selectLifeSupportCost = createSelector(
  [
    (state: RootState) => state.shipBuilder.stateroomConfig,
    (state: RootState) => state.shipBuilder.passengerConfig,
    (state: RootState) => state.optionalSystems.accommodations || [],
    (state: RootState) => state.optionalSystems.internal || [],
    selectAssignedCrewCount,
  ],
  (
    stateroomConfig,
    passengerConfig,
    accommodationSystems,
    internalSystems,
    assignedCrew,
  ): number => {
    // Base life support cost per Traveller rules
    let totalLifeSupportCost = 0;

    // Cr1000 per stateroom per Maintenance Period
    const totalStaterooms =
      (stateroomConfig?.single || 0) + (stateroomConfig?.double || 0) * 2;
    totalLifeSupportCost += totalStaterooms * 1000;

    // Cr1000 per person on board (crew + passengers, excluding low berth passengers)
    const totalPassengers =
      (passengerConfig?.high || 0) +
      (passengerConfig?.middle || 0) +
      (passengerConfig?.basic || 0);
    const totalPeople = assignedCrew + totalPassengers;
    totalLifeSupportCost += totalPeople * 1000;

    // Cr100 per occupied low berth
    const lowBerths = stateroomConfig?.lowBerths?.count || 0;
    const lowBerthPassengers = passengerConfig?.low || 0;
    const occupiedLowBerths = Math.min(lowBerths, lowBerthPassengers);
    totalLifeSupportCost += occupiedLowBerths * 100;

    // Add accommodation system life support costs
    const accommodationLifeSupportCost = Array.isArray(accommodationSystems)
      ? accommodationSystems.reduce((sum, system) => {
          const lifeSupportCost =
            typeof system.properties?.lifeSupportCost === "number"
              ? system.properties.lifeSupportCost
              : 0;
          return sum + lifeSupportCost;
        }, 0)
      : 0;
    totalLifeSupportCost += accommodationLifeSupportCost;

    // Calculate biosphere reduction (each ton eliminates life support for 2 passengers)
    const biosphereReduction = Array.isArray(internalSystems)
      ? internalSystems
          .filter((system) => system.subtype === "internalBiosphere")
          .reduce((total, system) => {
            if (typeof system.tonnage === "number") {
              // Each ton of biosphere eliminates life support cost for 2 passengers
              // This reduces the per-person cost (Cr1000) for up to 2 passengers per ton
              const passengerCapacity = system.tonnage * 2;
              const reduction = passengerCapacity * 1000; // Cr1000 per passenger per Maintenance Period
              return total + reduction;
            }
            return total;
          }, 0)
      : 0;

    // Return total cost minus biosphere reduction (minimum 0)
    return Math.max(0, totalLifeSupportCost - biosphereReduction);
  },
);

// Selector to count barracks accommodation toward Basic Passage requirements
export const selectBarracksBasicPassageCapacity = createSelector(
  [(state: RootState) => state.optionalSystems.accommodations || []],
  (accommodations) => {
    if (!accommodations || !Array.isArray(accommodations)) {
      return 0;
    }
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "barracks" &&
          s.properties?.designation === "passenger",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  },
);

// Selector to count barracks accommodation toward Crew accommodation requirements
export const selectBarracksCrewCapacity = createSelector(
  [(state: RootState) => state.optionalSystems.accommodations || []],
  (accommodations) => {
    if (!accommodations || !Array.isArray(accommodations)) {
      return 0;
    }
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "barracks" &&
          s.properties?.designation === "crew",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  },
);

// Selector to calculate crew accommodation needs
export const selectCrewAccommodationNeeds = createSelector(
  [selectAssignedCrewCount, selectBarracksCrewCapacity],
  (assignedCrew, barracksCrewCapacity) => {
    const crewNeedingStaterooms = Math.max(
      0,
      assignedCrew - barracksCrewCapacity,
    );
    return {
      totalCrew: assignedCrew,
      barracksCrewCapacity,
      crewNeedingStaterooms,
    };
  },
);

export const selectStateroomNeeds = createSelector(
  [
    (state: RootState) => state.shipBuilder.passengerConfig,
    selectBarracksBasicPassageCapacity,
  ],
  (passengerConfig, barracksBasicCapacity) => {
    const config = passengerConfig || {
      high: 0,
      middle: 0,
      basic: 0,
      low: 0,
      cargoOverrides: { high: 1, middle: 100, basic: 10, low: 10 },
    };
    const highCount = config.high || 0;
    const middleCount = config.middle || 0;
    const basicCount = config.basic || 0;
    const lowCount = config.low || 0;
    const highCargo = config.cargoOverrides?.high || 1;
    const middleCargo = config.cargoOverrides?.middle || 100;
    const basicCargo = config.cargoOverrides?.basic || 10;
    const lowCargo = config.cargoOverrides?.low || 10;

    // Calculate basic passage needs after accounting for barracks accommodation
    const basicPassengersNeedingStaterooms = Math.max(
      0,
      basicCount - barracksBasicCapacity,
    );
    const doubleStateroomsNeeded = Math.floor(
      basicPassengersNeedingStaterooms / 2,
    );
    const cotsNeeded = basicPassengersNeedingStaterooms % 2;

    return {
      singleNeeded: highCount + middleCount,
      doubleNeeded: doubleStateroomsNeeded,
      cotsNeeded: cotsNeeded,
      lowBerthsNeeded: lowCount,
      highCount,
      middleCount,
      basicCount,
      lowCount,
      highCargo,
      middleCargo,
      basicCargo,
      lowCargo,
      singleStateroomsNeeded: highCount + middleCount,
      doubleStateroomsNeeded: doubleStateroomsNeeded,
      barracksBasicCapacity,
      basicPassengersNeedingStaterooms,
    };
  },
);

export const selectInstalledSingleStaterooms = createSelector(
  [
    (state: RootState) => state.shipBuilder.stateroomConfig,
    (state: RootState) => state.optionalSystems.accommodations || [],
  ],
  (stateroomConfig, accommodations) => {
    const baseStaterooms = stateroomConfig?.single || 0;
    if (!accommodations || !Array.isArray(accommodations)) {
      return baseStaterooms;
    }
    const accommodationStaterooms = accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          (s.subtype === "highStateroom" || s.subtype === "luxuryStateroom"),
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
    return baseStaterooms + accommodationStaterooms;
  },
);

export const selectStateroomShortfall = createSelector(
  [selectInstalledSingleStaterooms, selectStateroomNeeds],
  (installed, needs) => installed - needs.singleNeeded,
);

// New selector to calculate basic passage accommodation status accounting for installed double staterooms
export const selectBasicPassageAccommodationStatus = createSelector(
  [
    (state: RootState) => state.shipBuilder.passengerConfig,
    (state: RootState) => state.shipBuilder.stateroomConfig,
    selectBarracksBasicPassageCapacity,
  ],
  (passengerConfig, stateroomConfig, barracksBasicCapacity) => {
    const basicCount = passengerConfig?.basic || 0;
    const installedDoubleStaterooms = stateroomConfig?.double || 0;

    // Calculate total accommodation available for basic passengers
    const barracksAccommodation = barracksBasicCapacity;
    const doubleStateroomAccommodation = installedDoubleStaterooms * 2; // Each double stateroom accommodates 2 people
    const totalAccommodation =
      barracksAccommodation + doubleStateroomAccommodation;

    // Calculate how many basic passengers still need accommodation
    const basicPassengersNeedingAccommodation = Math.max(
      0,
      basicCount - totalAccommodation,
    );

    // Calculate how many additional double staterooms are still needed
    const additionalDoubleStateroomsNeeded = Math.floor(
      basicPassengersNeedingAccommodation / 2,
    );
    const cotsNeeded = basicPassengersNeedingAccommodation % 2;

    return {
      totalBasicPassengers: basicCount,
      barracksAccommodation,
      doubleStateroomAccommodation,
      totalAccommodation,
      basicPassengersNeedingAccommodation,
      additionalDoubleStateroomsNeeded,
      cotsNeeded,
      isFullyAccommodated: basicPassengersNeedingAccommodation === 0,
    };
  },
);

// New selector to calculate low berth accommodation status
export const selectLowBerthAccommodationStatus = createSelector(
  [
    (state: RootState) => state.shipBuilder.passengerConfig,
    (state: RootState) => state.shipBuilder.stateroomConfig,
  ],
  (passengerConfig, stateroomConfig) => {
    const lowCount = passengerConfig?.low || 0;
    const installedLowBerths = stateroomConfig?.lowBerths?.count || 0;

    // Calculate how many low berths are still needed
    const lowBerthsNeeded = Math.max(0, lowCount - installedLowBerths);

    return {
      totalLowPassengers: lowCount,
      installedLowBerths,
      lowBerthsNeeded,
      isFullyAccommodated: lowBerthsNeeded === 0,
    };
  },
);

// New selector to calculate hierarchical single stateroom allocation
export const selectSingleStateroomAllocationStatus = createSelector(
  [
    (state: RootState) => state.shipBuilder.passengerConfig,
    (state: RootState) => state.shipBuilder.stateroomConfig,
    (state: RootState) => state.optionalSystems.accommodations || [],
    selectAssignedCrewCount,
    selectBarracksCrewCapacity,
  ],
  (
    passengerConfig,
    stateroomConfig,
    accommodations,
    assignedCrewCount,
    barracksCrewCapacity,
  ) => {
    const highCount = passengerConfig?.high || 0;
    const middleCount = passengerConfig?.middle || 0;
    const baseSingleStaterooms = stateroomConfig?.single || 0;

    // Get optional staterooms (High Staterooms and Luxury Staterooms)
    const optionalStaterooms = accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          (s.subtype === "highStateroom" || s.subtype === "luxuryStateroom"),
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );

    // Total single staterooms includes both base and optional staterooms
    const totalSingleStaterooms = baseSingleStaterooms + optionalStaterooms;

    // Calculate crew needing staterooms (crew not accommodated by barracks)
    const crewNeedingStaterooms = Math.max(
      0,
      assignedCrewCount - barracksCrewCapacity,
    );

    // Allocate staterooms in priority order: Crew → High Passage → Middle Passage
    let remainingStaterooms = totalSingleStaterooms;

    // 1. Allocate to crew first (highest priority)
    const crewAllocated = Math.min(crewNeedingStaterooms, remainingStaterooms);
    remainingStaterooms -= crewAllocated;

    // 2. Allocate to high passage passengers
    const highAllocated = Math.min(highCount, remainingStaterooms);
    remainingStaterooms -= highAllocated;

    // 3. Allocate to middle passage passengers
    const middleAllocated = Math.min(middleCount, remainingStaterooms);
    remainingStaterooms -= middleAllocated;

    // Calculate shortfalls
    const crewShortfall = Math.max(0, crewNeedingStaterooms - crewAllocated);
    const highShortfall = Math.max(0, highCount - highAllocated);
    const middleShortfall = Math.max(0, middleCount - middleAllocated);

    return {
      totalSingleStaterooms,
      baseSingleStaterooms,
      optionalStaterooms,
      crewNeedingStaterooms,
      crewAllocated,
      crewShortfall,
      crewFullyAccommodated: crewShortfall === 0,
      highPassengers: highCount,
      highAllocated,
      highShortfall,
      highFullyAccommodated: highShortfall === 0,
      middlePassengers: middleCount,
      middleAllocated,
      middleShortfall,
      middleFullyAccommodated: middleShortfall === 0,
      remainingStaterooms,
    };
  },
);

// Comprehensive hierarchical stateroom allocation selector
export const selectHierarchicalStateroomAllocation = createSelector(
  [
    (state: RootState) => state.shipBuilder.passengerConfig,
    (state: RootState) => state.shipBuilder.stateroomConfig,
    (state: RootState) => state.optionalSystems.accommodations || [],
    selectAssignedCrewCount,
    selectBarracksCrewCapacity,
  ],
  (
    passengerConfig,
    stateroomConfig,
    accommodations,
    assignedCrewCount,
    barracksCrewCapacity,
  ) => {
    const highCount = passengerConfig?.high || 0;
    const middleCount = passengerConfig?.middle || 0;
    const basicCount = passengerConfig?.basic || 0;
    const lowCount = passengerConfig?.low || 0;
    
    const baseSingleStaterooms = stateroomConfig?.single || 0;
    const baseDoubleStaterooms = stateroomConfig?.double || 0;
    const baseLowBerths = stateroomConfig?.lowBerths?.count || 0;

    // Get optional accommodation systems
    const luxuryStaterooms = accommodations
      .filter(s => s?.type === "accommodation" && s.subtype === "luxuryStateroom")
      .reduce((sum, s) => sum + (typeof s.properties?.quantity === "number" ? s.properties.quantity : 1), 0);
    
    const highStaterooms = accommodations
      .filter(s => s?.type === "accommodation" && s.subtype === "highStateroom")
      .reduce((sum, s) => sum + (typeof s.properties?.quantity === "number" ? s.properties.quantity : 1), 0);
    
    const barracks = accommodations
      .filter(s => s?.type === "accommodation" && s.subtype === "barracks")
      .reduce((sum, s) => sum + (typeof s.properties?.capacity === "number" ? s.properties.capacity : 0), 0);

    // Calculate crew needing staterooms (crew not accommodated by barracks)
    const crewNeedingStaterooms = Math.max(0, assignedCrewCount - barracksCrewCapacity);

    // Initialize allocation tracking
    let remainingLuxury = luxuryStaterooms;
    let remainingHigh = highStaterooms;
    let remainingSingle = baseSingleStaterooms;
    let remainingDouble = baseDoubleStaterooms;
    let remainingBarracks = barracks;
    let remainingLowBerths = baseLowBerths;

    // Track allocations for each group
    const allocations = {
      high: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0, cots: 0 },
      middle: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0, cots: 0 },
      basic: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0, cots: 0 },
      crew: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0 },
      low: { lowBerths: 0 }
    };

    // Track how many people are actually accommodated by each type
    const accommodated = {
      high: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0, cots: 0 },
      middle: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0, cots: 0 },
      basic: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0, cots: 0 },
      crew: { luxury: 0, high: 0, single: 0, double: 0, barracks: 0 },
      low: { lowBerths: 0 }
    };

    // ALLOCATION HIERARCHY:
    // High Passage: Luxury Stateroom → High Stateroom → Single Stateroom
    // Middle Passage: Luxury Stateroom → High Stateroom → Single Stateroom
    // Basic Passage: Luxury Stateroom → High Stateroom → Single Stateroom → Double Stateroom → Barracks → Cargo Space Cots
    // Crew: Luxury Stateroom → High Stateroom → Single Stateroom → Double Stateroom → Barracks
    // Low Berth: Low Berth

    // 1. Allocate Low Berths (simplest - direct allocation)
    const lowAllocated = Math.min(lowCount, remainingLowBerths);
    allocations.low.lowBerths = lowAllocated;
    accommodated.low.lowBerths = lowAllocated;
    remainingLowBerths -= lowAllocated;

    // 2. Allocate High Passage (highest priority for premium accommodation)
    let highRemaining = highCount;
    
    // High Passage: Luxury → High → Single
    const highLuxury = Math.min(highRemaining, remainingLuxury);
    allocations.high.luxury = highLuxury;
    accommodated.high.luxury = highLuxury;
    highRemaining -= highLuxury;
    remainingLuxury -= highLuxury;
    
    const highHigh = Math.min(highRemaining, remainingHigh);
    allocations.high.high = highHigh;
    accommodated.high.high = highHigh;
    highRemaining -= highHigh;
    remainingHigh -= highHigh;
    
    const highSingle = Math.min(highRemaining, remainingSingle);
    allocations.high.single = highSingle;
    accommodated.high.single = highSingle;
    highRemaining -= highSingle;
    remainingSingle -= highSingle;

    // 3. Allocate Middle Passage
    let middleRemaining = middleCount;
    
    // Middle Passage: Luxury → High → Single
    const middleLuxury = Math.min(middleRemaining, remainingLuxury);
    allocations.middle.luxury = middleLuxury;
    accommodated.middle.luxury = middleLuxury;
    middleRemaining -= middleLuxury;
    remainingLuxury -= middleLuxury;
    
    const middleHigh = Math.min(middleRemaining, remainingHigh);
    allocations.middle.high = middleHigh;
    accommodated.middle.high = middleHigh;
    middleRemaining -= middleHigh;
    remainingHigh -= middleHigh;
    
    const middleSingle = Math.min(middleRemaining, remainingSingle);
    allocations.middle.single = middleSingle;
    accommodated.middle.single = middleSingle;
    middleRemaining -= middleSingle;
    remainingSingle -= middleSingle;

    // 4. Allocate Basic Passage
    let basicRemaining = basicCount;
    
    // Basic Passage: Luxury → High → Single → Double → Barracks → Cots
    const basicLuxury = Math.min(basicRemaining, remainingLuxury);
    allocations.basic.luxury = basicLuxury;
    accommodated.basic.luxury = basicLuxury;
    basicRemaining -= basicLuxury;
    remainingLuxury -= basicLuxury;
    
    const basicHigh = Math.min(basicRemaining, remainingHigh);
    allocations.basic.high = basicHigh;
    accommodated.basic.high = basicHigh;
    basicRemaining -= basicHigh;
    remainingHigh -= basicHigh;
    
    const basicSingle = Math.min(basicRemaining, remainingSingle);
    allocations.basic.single = basicSingle;
    accommodated.basic.single = basicSingle;
    basicRemaining -= basicSingle;
    remainingSingle -= basicSingle;
    
    const basicDouble = Math.min(basicRemaining, remainingDouble * 2); // Double staterooms can accommodate 1 or 2 people
    const basicDoubleStateroomsNeeded = Math.ceil(basicDouble / 2); // Calculate how many double staterooms we need
    allocations.basic.double = Math.min(basicDoubleStateroomsNeeded, remainingDouble);
    accommodated.basic.double = basicDouble; // Track how many people are actually accommodated
    basicRemaining -= basicDouble;
    remainingDouble -= allocations.basic.double;
    
    const basicBarracks = Math.min(basicRemaining, remainingBarracks);
    allocations.basic.barracks = basicBarracks;
    accommodated.basic.barracks = basicBarracks;
    basicRemaining -= basicBarracks;
    remainingBarracks -= basicBarracks;
    
    // Any remaining basic passengers go to cargo space cots
    allocations.basic.cots = basicRemaining;
    accommodated.basic.cots = basicRemaining;

    // 5. Allocate Crew (highest priority for remaining accommodation)
    let crewRemaining = crewNeedingStaterooms;
    
    // Crew: Luxury → High → Single → Double → Barracks
    const crewLuxury = Math.min(crewRemaining, remainingLuxury);
    allocations.crew.luxury = crewLuxury;
    accommodated.crew.luxury = crewLuxury;
    crewRemaining -= crewLuxury;
    remainingLuxury -= crewLuxury;
    
    const crewHigh = Math.min(crewRemaining, remainingHigh);
    allocations.crew.high = crewHigh;
    accommodated.crew.high = crewHigh;
    crewRemaining -= crewHigh;
    remainingHigh -= crewHigh;
    
    const crewSingle = Math.min(crewRemaining, remainingSingle);
    allocations.crew.single = crewSingle;
    accommodated.crew.single = crewSingle;
    crewRemaining -= crewSingle;
    remainingSingle -= crewSingle;
    
    const crewDouble = Math.min(crewRemaining, remainingDouble * 2); // Double staterooms can accommodate 1 or 2 people
    const crewDoubleStateroomsNeeded = Math.ceil(crewDouble / 2); // Calculate how many double staterooms we need
    allocations.crew.double = Math.min(crewDoubleStateroomsNeeded, remainingDouble);
    accommodated.crew.double = crewDouble; // Track how many people are actually accommodated
    crewRemaining -= crewDouble;
    remainingDouble -= allocations.crew.double;
    
    const crewBarracks = Math.min(crewRemaining, remainingBarracks);
    allocations.crew.barracks = crewBarracks;
    accommodated.crew.barracks = crewBarracks;
    crewRemaining -= crewBarracks;
    remainingBarracks -= crewBarracks;

    // Calculate totals and shortfalls using accommodated numbers
    const highTotal = accommodated.high.luxury + accommodated.high.high + accommodated.high.single;
    const middleTotal = accommodated.middle.luxury + accommodated.middle.high + accommodated.middle.single;
    const basicTotal = accommodated.basic.luxury + accommodated.basic.high + accommodated.basic.single + 
                      accommodated.basic.double + accommodated.basic.barracks + accommodated.basic.cots;
    const crewTotal = accommodated.crew.luxury + accommodated.crew.high + accommodated.crew.single + 
                     accommodated.crew.double + accommodated.crew.barracks;

    return {
      // Available accommodation
      available: {
        luxury: luxuryStaterooms,
        high: highStaterooms,
        single: baseSingleStaterooms,
        double: baseDoubleStaterooms,
        barracks: barracks,
        lowBerths: baseLowBerths
      },
      
      // Allocations by type (number of staterooms)
      allocations,
      
      // Accommodated by type (number of people)
      accommodated,
      
      // Totals allocated
      totals: {
        high: highTotal,
        middle: middleTotal,
        basic: basicTotal,
        crew: crewTotal,
        low: allocations.low.lowBerths
      },
      
      // Shortfalls
      shortfalls: {
        high: Math.max(0, highCount - highTotal),
        middle: Math.max(0, middleCount - middleTotal),
        basic: Math.max(0, basicCount - basicTotal),
        crew: Math.max(0, crewNeedingStaterooms - crewTotal),
        low: Math.max(0, lowCount - allocations.low.lowBerths)
      },
      
      // Status flags
      status: {
        highFullyAccommodated: highCount <= highTotal,
        middleFullyAccommodated: middleCount <= middleTotal,
        basicFullyAccommodated: basicCount <= basicTotal,
        crewFullyAccommodated: crewNeedingStaterooms <= crewTotal,
        lowFullyAccommodated: lowCount <= allocations.low.lowBerths
      },
      
      // Remaining accommodation
      remaining: {
        luxury: remainingLuxury,
        high: remainingHigh,
        single: remainingSingle,
        double: remainingDouble,
        barracks: remainingBarracks,
        lowBerths: remainingLowBerths
      }
    };
  },
);

export const selectTotalPassengerCargoTons = createSelector(
  [(state: RootState) => state.shipBuilder.passengerConfig],
  (passengerConfig) => {
    const config = passengerConfig || {
      high: 0,
      middle: 0,
      basic: 0,
      low: 0,
      cargoOverrides: { high: 1, middle: 100, basic: 10, low: 10 },
    };
    const highCount = config.high || 0;
    const middleCount = config.middle || 0;
    const basicCount = config.basic || 0;
    const lowCount = config.low || 0;
    const highCargo = config.cargoOverrides?.high || 1;
    const middleCargo = config.cargoOverrides?.middle || 100;
    const basicCargo = config.cargoOverrides?.basic || 10;
    const lowCargo = config.cargoOverrides?.low || 10;
    return (
      highCount * highCargo +
      (middleCount * middleCargo) / 1000 +
      (basicCount * basicCargo) / 1000 +
      (lowCount * lowCargo) / 1000
    );
  },
);

export const selectTotalStaterooms = createSelector(
  [
    (state: RootState) => state.shipBuilder.stateroomConfig,
    (state: RootState) => state.optionalSystems.accommodations || [],
  ],
  (stateroomConfig, accommodations) => {
    const baseStaterooms =
      (stateroomConfig?.single || 0) + (stateroomConfig?.double || 0);

    // Add high staterooms and luxury staterooms (these are staterooms)
    const optionalStaterooms = accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          (s.subtype === "highStateroom" || s.subtype === "luxuryStateroom"),
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );

    return baseStaterooms + optionalStaterooms;
  },
);

export const selectTotalStateroomCapacity = createSelector(
  [
    (state: RootState) => state.shipBuilder.stateroomConfig,
    (state: RootState) => state.optionalSystems.accommodations || [],
  ],
  (stateroomConfig, accommodations) => {
    const baseCapacity =
      (stateroomConfig?.single || 0) + (stateroomConfig?.double || 0) * 2;

    // Add high staterooms and luxury staterooms (1 person each)
    const optionalStateroomCapacity = accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          (s.subtype === "highStateroom" || s.subtype === "luxuryStateroom"),
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );

    // Add barracks capacity (multiple people per barracks)
    const barracksCapacity = accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "barracks",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.tonnage === "number"
            ? s.tonnage
            : 0),
        0,
      );

    return baseCapacity + optionalStateroomCapacity + barracksCapacity;
  },
);

// Selector to get total tonnage of common areas from optional systems
export const selectOptionalSystemsCommonAreaTonnage = createSelector(
  [(state: RootState) => state.optionalSystems.commonAreas || []],
  (commonAreas) => {
    return commonAreas
      .filter((s) => s && typeof s === "object" && s.type === "commonArea")
      .reduce((sum: number, s) => {
        // Handle different common area types with their specific tonnage calculations
        const properties = s.properties;
        switch (s.subtype) {
          case "swimmingPool":
            return sum + (Number(properties?.size) || 0);
          case "theatreBasic":
          case "theatreAdvanced":
            return sum + (Number(properties?.size) || 0);
          case "brewery":
            return sum + (Number(properties?.size) || 0);
          case "gourmetKitchen":
            return sum + (Number(properties?.diners) || 0); // 1 ton per diner
          case "hotTub":
            return sum + (Number(properties?.users) || 0) * 0.25; // 0.25 tons per user
          case "zeroGRoom":
            return sum + (Number(properties?.size) || 0);
          default:
            return sum + (s.tonnage || 0);
        }
      }, 0);
  },
);

export const selectRecommendedCommonArea = createSelector(
  [
    selectTotalStaterooms,
    (state: RootState) => state.shipBuilder.stateroomConfig,
  ],
  (totalStaterooms, stateroomConfig) => {
    // Use STANDARD stateroom tonnage (assume 4 tons if not available)
    const stateroomTons = 4;
    return totalStaterooms * stateroomTons * 0.25;
  },
);

export const selectStateroomComfort = createSelector(
  [
    (state: RootState) => state.shipBuilder.stateroomConfig,
    selectRecommendedCommonArea,
  ],
  (stateroomConfig, recommendedCommonArea) => {
    return Math.min(
      1,
      (stateroomConfig?.commonArea || 0) / (recommendedCommonArea || 1),
    );
  },
);

export const selectShowCommonAreaWarning = createSelector(
  [
    (state: RootState) => state.shipBuilder.stateroomConfig,
    selectRecommendedCommonArea,
    selectOptionalSystemsCommonAreaTonnage,
  ],
  (stateroomConfig, recommendedCommonArea, optionalCommonAreaTonnage) => {
    const manualCommonArea = stateroomConfig?.commonArea || 0;
    const totalCommonArea = manualCommonArea + optionalCommonAreaTonnage;
    return totalCommonArea < recommendedCommonArea;
  },
);

// Crew and drive presence selectors
export const selectHasJumpDrive = createSelector(
  [(state: RootState) => state.shipBuilder.drives],
  (drives) => drives.some((d) => d.type === DriveType.JUMP && d.rating > 0),
);

export const selectHasManeuverDrive = createSelector(
  [(state: RootState) => state.shipBuilder.drives],
  (drives) => drives.some((d) => d.type === DriveType.MANEUVER && d.rating > 0),
);

// Selector to get the effective maneuver drive rating (accounting for concealed drives)
export const selectEffectiveManeuverDriveRating = createSelector(
  [
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.optionalSystems.drives,
  ],
  (drives, optionalDrives) => {
    const maneuverDrive = drives.find((d) => d.type === "maneuver");
    if (!maneuverDrive) return 0;

    const baseRating = maneuverDrive.rating;
    const concealedDrive = optionalDrives.find(
      (d) => d.subtype === "concealed",
    );

    if (concealedDrive) {
      // Apply thrust penalty: halve the rating and round down
      return Math.floor(baseRating * 0.5);
    }

    return baseRating;
  },
);

// Selector to check if concealed maneuver drive is installed
export const selectHasConcealedManeuverDrive = createSelector(
  [(state: RootState) => state.optionalSystems.drives],
  (optionalDrives) => optionalDrives.some((d) => d.subtype === "concealed"),
);

export const selectTotalPassengerCount = createSelector(
  [(state: RootState) => state.shipBuilder.passengerConfig],
  (passengerConfig) => {
    const config = passengerConfig || {
      high: 0,
      middle: 0,
      basic: 0,
      low: 0,
      cargoOverrides: { high: 1, middle: 100, basic: 10, low: 10 },
    };
    return (["high", "middle", "basic", "low"] as const)
      .map((key) => config[key] || 0)
      .reduce((a, b) => a + b, 0);
  },
);

export const selectCrewRequirements = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize,
    selectHasJumpDrive,
    selectHasManeuverDrive,
    (state: RootState) => state.shipBuilder.smallCraftCount || 0,
    (state: RootState) => state.shipBuilder.weaponCounts || {},
    selectTotalPassengerCount,
  ],
  (
    shipSize,
    hasJumpDrive,
    hasManeuverDrive,
    smallCraftCount,
    weaponCounts,
    totalPassengers,
  ) => {
    const requirements: Record<CrewRole, number> = {} as Record<
      CrewRole,
      number
    >;
    Object.values(CrewRole).forEach((role) => {
      requirements[role] = 0;
    });

    Object.entries(CREW_TYPES).forEach(([type, crewType]) => {
      // Convert the string key back to the enum value
      const role = Object.values(CrewRole).find(r => r === type) as CrewRole;
      if (!role) return; // Skip if we can't find the role
      
      let required = crewType.baseRequired(shipSize);

      if (crewType.jumpDriveRequired && hasJumpDrive) {
        required += crewType.jumpDriveRequired;
      }

      if (crewType.maneuverDriveRequired && hasManeuverDrive) {
        required += crewType.maneuverDriveRequired;
      }

      if (crewType.smallCraftRequired && smallCraftCount > 0) {
        required += crewType.smallCraftRequired * smallCraftCount;
      }

      if (
        crewType.weaponsRequired &&
        Object.values(weaponCounts).some((count) => count > 0)
      ) {
        required +=
          crewType.weaponsRequired *
          Object.values(weaponCounts).reduce((a, b) => a + b, 0);
      }

      if (crewType.passengersRequired && totalPassengers > 0) {
        required += crewType.passengersRequired * totalPassengers;
      }

      requirements[role] = Math.max(0, required);
    });

    return requirements;
  },
);

export const selectCrewDescriptions = createSelector(
  [
    selectHasJumpDrive,
    selectHasManeuverDrive,
    (state: RootState) => state.shipBuilder.smallCraftCount || 0,
  ],
  (hasJumpDrive, hasManeuverDrive, smallCraftCount) => {
    const descriptions: Record<CrewRole, string> = {} as Record<
      CrewRole,
      string
    >;
    Object.values(CrewRole).forEach((role) => {
      descriptions[role] = "";
    });

    Object.entries(CREW_TYPES).forEach(([type, crewType]) => {
      // Convert the string key back to the enum value
      const role = Object.values(CrewRole).find(r => r === type) as CrewRole;
      if (!role) return; // Skip if we can't find the role
      
      const requirements = [];

      if (crewType.jumpDriveRequired) {
        requirements.push(`${crewType.jumpDriveRequired} per jump drive`);
      }
      if (crewType.maneuverDriveRequired) {
        requirements.push(
          `${crewType.maneuverDriveRequired} per maneuver drive`,
        );
      }
      if (crewType.smallCraftRequired) {
        requirements.push(`${crewType.smallCraftRequired} per small craft`);
      }
      if (crewType.weaponsRequired) {
        requirements.push(`${crewType.weaponsRequired} per weapon`);
      }
      if (crewType.passengersRequired) {
        requirements.push(`${crewType.passengersRequired} per passenger`);
      }

      descriptions[role] =
        requirements.length > 0
          ? `Required: ${requirements.join(", ")}`
          : crewType.description;
    });

    return descriptions;
  },
);

export const selectCurrentCrewCounts = createSelector(
  [(state: RootState) => state.shipBuilder.crewComposition],
  (crewComposition) => {
    const composition = crewComposition || {
      captain: 0,
      pilot: 0,
      engineer: 0,
      gunner: 0,
      medic: 0,
      astrogator: 0,
      steward: 0,
      maintenance: 0,
      administrator: 0,
      sensorOperator: 0,
      officer: 0,
      marines: 0,
    };
    return {
      [CrewRole.CAPTAIN]: composition.captain || 0,
      [CrewRole.PILOT]: composition.pilot || 0,
      [CrewRole.ENGINEER]: composition.engineer || 0,
      [CrewRole.GUNNER]: composition.gunner || 0,
      [CrewRole.MEDIC]: composition.medic || 0,
      [CrewRole.ASTROGATOR]: composition.astrogator || 0,
      [CrewRole.STEWARD]: composition.steward || 0,
      [CrewRole.MAINTENANCE]: composition.maintenance || 0,
      [CrewRole.ADMINISTRATOR]: composition.administrator || 0,
      [CrewRole.SENSOR_OPERATOR]: composition.sensorOperator || 0,
      [CrewRole.OFFICER]: composition.officer || 0,
    } as Record<CrewRole, number>;
  },
);

// Cargo system selectors

export const selectTotalExternalCargoCapacity = createSelector(
  [(state: RootState) => state.optionalSystems.cargo || []],
  (cargoSystems) => {
    const externalCargoMounts = cargoSystems.filter(
      (s) => s.subtype === "externalCargoMount",
    );
    return externalCargoMounts.reduce(
      (sum, s) => sum + (s.properties?.externalCargoCapacity || 0),
      0,
    );
  },
);

export const selectTotalJumpNetCargoCapacity = createSelector(
  [(state: RootState) => state.optionalSystems.cargo || []],
  (cargoSystems) => {
    const jumpNets = cargoSystems.filter(
      (s) =>
        s.subtype === "jumpNetInterplanetary" ||
        s.subtype === "jumpNetInterstellar",
    );
    return jumpNets.reduce((sum, s) => {
      const cargoSystem = s as CargoSystem;
      return sum + (cargoSystem.properties?.jumpNetCargoCapacity || 0);
    }, 0);
  },
);


export const selectExternalCargoMountStats = createSelector(
  [(state: RootState) => state.optionalSystems.cargo],
  (cargoSystems) => {
    const externalCargoCapacity = cargoSystems
      .filter((system) => system.subtype === "externalCargoMount")
      .reduce(
        (total, system) =>
          total + (system.properties?.externalCargoCapacity || 0),
        0,
      );
    // Use the actual calculation function from utils
    const cost = externalCargoCapacity * 1000; // Cr1000 per ton

    return {
      capacity: externalCargoCapacity,
      cost,
      tonnage: 0, // No additional tonnage, uses external space
      power: 0,
      techLevel: 8,
      maintenanceCost: cost * 0.01, // 1% of cost
    };
  },
);

export const selectJumpNetStats = createSelector(
  [(state: RootState) => state.optionalSystems.cargo],
  (cargoSystems) => {
    const jumpNetSystem = cargoSystems.find(
      (system) =>
        system.subtype === "jumpNetInterplanetary" ||
        system.subtype === "jumpNetInterstellar",
    );
    const jumpNetCargoCapacity = jumpNetSystem?.properties?.cargoSpace || 0;
    const jumpNetType = jumpNetSystem?.subtype || "jumpNetInterplanetary";
    const isInterstellar = jumpNetType === "jumpNetInterstellar";

    // Use the actual calculation function from utils
    const cost = jumpNetCargoCapacity * (isInterstellar ? 2000 : 1000); // Cr2000/Cr1000 per ton
    const tonnage = jumpNetCargoCapacity * 0.05; // 5% of capacity
    const power = isInterstellar ? 2 : 1; // 2/1 Power

    return {
      capacity: jumpNetCargoCapacity,
      cost,
      tonnage,
      power,
      techLevel: isInterstellar ? 12 : 10,
      maintenanceCost: cost * 0.01, // 1% of cost
      type: jumpNetType,
      isInterstellar,
    };
  },
);

// Drive potential calculations for cargo systems
export const selectDrivePotentialCalculations = createSelector(
  [
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => selectTotalExternalCargoCapacity(state),
  ],
  (drives, shipSize, totalExternalCargo) => {
    // Traveller Thrust Potential Table (rating: required % of hull)
    const THRUST_POTENTIAL: {
      [rating: number]: { percent: number; tl: number };
    } = {
      0: { percent: 0.005, tl: 9 },
      1: { percent: 0.01, tl: 9 },
      2: { percent: 0.02, tl: 10 },
      3: { percent: 0.03, tl: 10 },
      4: { percent: 0.04, tl: 11 },
      5: { percent: 0.05, tl: 11 },
      6: { percent: 0.06, tl: 12 },
      7: { percent: 0.07, tl: 13 },
      8: { percent: 0.08, tl: 14 },
      9: { percent: 0.09, tl: 15 },
      10: { percent: 0.1, tl: 16 },
      11: { percent: 0.11, tl: 17 },
    };

    // Traveller Jump Drive Potential Table (rating: required % of hull, plus 5 tons)
    const JUMP_POTENTIAL: {
      [rating: number]: { percent: number; tl: number };
    } = {
      1: { percent: 0.025, tl: 9 },
      2: { percent: 0.05, tl: 11 },
      3: { percent: 0.075, tl: 12 },
      4: { percent: 0.1, tl: 13 },
      5: { percent: 0.125, tl: 14 },
      6: { percent: 0.15, tl: 15 },
      7: { percent: 0.175, tl: 16 },
      8: { percent: 0.2, tl: 17 },
      9: { percent: 0.225, tl: 18 },
    };

    // Traveller Reaction Drive Potential Table (rating: required % of hull)
    const REACTION_POTENTIAL: {
      [rating: number]: { percent: number; tl: number };
    } = {
      0: { percent: 0.01, tl: 7 },
      1: { percent: 0.02, tl: 7 },
      2: { percent: 0.04, tl: 7 },
      3: { percent: 0.06, tl: 7 },
      4: { percent: 0.08, tl: 8 },
      5: { percent: 0.1, tl: 8 },
      6: { percent: 0.12, tl: 8 },
      7: { percent: 0.14, tl: 9 },
      8: { percent: 0.16, tl: 9 },
      9: { percent: 0.18, tl: 9 },
      10: { percent: 0.2, tl: 10 },
      11: { percent: 0.22, tl: 10 },
      12: { percent: 0.24, tl: 10 },
      13: { percent: 0.26, tl: 11 },
      14: { percent: 0.28, tl: 11 },
      15: { percent: 0.3, tl: 11 },
      16: { percent: 0.32, tl: 12 },
    };

    const previewEffectiveTonnage = shipSize + totalExternalCargo;
    const previewManeuverDrives = drives.filter((d) => d.type === "maneuver");
    const previewManeuverDriveTons = previewManeuverDrives.reduce(
      (sum, d) =>
        sum +
        (d.rating > 0 ? d.rating : 0) *
          shipSize *
          (THRUST_POTENTIAL[d.rating]?.percent || 0),
      0,
    );
    const previewJumpDrives = drives.filter((d) => d.type === "jump");
    const previewJumpDriveTons = previewJumpDrives.reduce(
      (sum, d) =>
        sum +
        (d.rating > 0 ? d.rating : 0) *
          shipSize *
          (JUMP_POTENTIAL[d.rating]?.percent || 0) +
        (JUMP_POTENTIAL[d.rating] ? 5 : 0),
      0,
    );
    const previewReactionDrives = drives.filter((d) => d.type === "reaction");
    const previewReactionDriveTons = previewReactionDrives.reduce(
      (sum, d) =>
        sum +
        (d.rating > 0 ? d.rating : 0) *
          shipSize *
          (REACTION_POTENTIAL[d.rating]?.percent || 0),
      0,
    );

    return {
      previewEffectiveTonnage,
      previewManeuverDrives,
      previewManeuverDriveTons,
      previewJumpDrives,
      previewJumpDriveTons,
      previewReactionDrives,
      previewReactionDriveTons,
      THRUST_POTENTIAL,
      JUMP_POTENTIAL,
      REACTION_POTENTIAL,
    };
  },
);

// Effective drive ratings when external cargo or jump nets are present
export const selectEffectiveDriveRatings = createSelector(
  [
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => selectTotalExternalCargoCapacity(state),
    (state: RootState) => selectTotalJumpNetCargoCapacity(state),
    (state: RootState) => selectTotalExternalSmallCraftTonnage(state),
  ],
  (drives, shipSize, totalExternalCargo, totalJumpNetCargo, totalExternalSmallCraft) => {
    const totalAdditionalCargo = totalExternalCargo + totalJumpNetCargo;

    if (totalAdditionalCargo === 0) {
      // No additional cargo, return base ratings
      const maneuverDrive = drives.find((d) => d.type === "maneuver");
      const jumpDrive = drives.find((d) => d.type === "jump");
      const reactionDrive = drives.find((d) => d.type === "reaction");

      return {
        hasExternalCargo: false,
        hasJumpNet: false,
        baseManeuverRating: maneuverDrive?.rating || 0,
        effectiveManeuverRating: maneuverDrive?.rating || 0,
        baseJumpRating: jumpDrive?.rating || 0,
        effectiveJumpRating: jumpDrive?.rating || 0,
        baseReactionRating: reactionDrive?.rating || 0,
        effectiveReactionRating: reactionDrive?.rating || 0,
        effectiveTonnage: shipSize,
        externalCargoTonnage: 0,
        jumpNetCargoTonnage: 0,
      };
    }

    // Calculate effective ratings with additional cargo (external cargo + jump nets + external small craft)
    const effectiveTonnage = shipSize + totalAdditionalCargo + totalExternalSmallCraft;
    const maneuverDrive = drives.find((d) => d.type === "maneuver");
    const jumpDrive = drives.find((d) => d.type === "jump");
    const reactionDrive = drives.find((d) => d.type === "reaction");

    // Calculate what the drives can actually achieve with the increased tonnage
    let effectiveManeuverRating = 0;
    if (maneuverDrive && maneuverDrive.rating > 0) {
      // Calculate what thrust rating the drive can actually provide at the effective tonnage
      const driveTonnage = maneuverDrive.rating * shipSize * 0.1; // 10% of ship size per thrust
      effectiveManeuverRating = Math.floor(
        driveTonnage / (effectiveTonnage * 0.1),
      );
    }

    let effectiveJumpRating = 0;
    if (jumpDrive && jumpDrive.rating > 0) {
      // Calculate what jump rating the drive can actually provide at the effective tonnage
      const driveTonnage = jumpDrive.rating * shipSize * 0.1 + 5; // 10% of ship size per jump + 5 tons
      effectiveJumpRating = Math.floor(
        (driveTonnage - 5) / (effectiveTonnage * 0.1),
      );
    }

    let effectiveReactionRating = 0;
    if (reactionDrive && reactionDrive.rating > 0) {
      // Calculate what thrust rating the reaction drive can actually provide at the effective tonnage
      const driveTonnage = reactionDrive.rating * shipSize * 0.05; // 5% of ship size per thrust
      effectiveReactionRating = Math.floor(
        driveTonnage / (effectiveTonnage * 0.05),
      );
    }

    return {
      hasExternalCargo: totalExternalCargo > 0,
      hasJumpNet: totalJumpNetCargo > 0,
      hasExternalSmallCraft: totalExternalSmallCraft > 0,
      baseManeuverRating: maneuverDrive?.rating || 0,
      effectiveManeuverRating: Math.max(0, effectiveManeuverRating),
      baseJumpRating: jumpDrive?.rating || 0,
      effectiveJumpRating: Math.max(0, effectiveJumpRating),
      baseReactionRating: reactionDrive?.rating || 0,
      effectiveReactionRating: Math.max(0, effectiveReactionRating),
      effectiveTonnage,
      externalCargoTonnage: totalExternalCargo,
      jumpNetCargoTonnage: totalJumpNetCargo,
      externalSmallCraftTonnage: totalExternalSmallCraft,
    };
  },
);

// Sensor system selectors
export const selectDeepPenetrationScannersSpecs = createSelector(
  [(state: RootState, tons: number) => tons],
  (tons) => {
    // Use the actual calculation function from utils
    // Import at top: import { calculateDeepPenetrationScannersSpecs } from '../../utils/calculations/optionalSystems';
    // (Assume already imported)
    return calculateDeepPenetrationScannersSpecs(tons);
  },
);

export const selectExtensionNetSpecs = createSelector(
  [(state: RootState) => state.shipBuilder.shipSize],
  (shipTonnage) => {
    return calculateExtensionNetSpecs(shipTonnage);
  },
);

export const selectDistributedArraySpecs = createSelector(
  [
    (
      state: RootState,
      baseSuite: { tonnage: number; cost: number; power: number },
    ) => baseSuite,
    (state: RootState) => state.shipBuilder.shipSize,
  ],
  (baseSuite, shipTonnage) => {
    return calculateDistributedArraySpecs(baseSuite);
  },
);

export const selectExtendedArraySpecs = createSelector(
  [
    (
      state: RootState,
      baseSuite: { tonnage: number; cost: number; power: number },
    ) => baseSuite,
  ],
  (baseSuite) => {
    return calculateExtendedArraySpecs(baseSuite);
  },
);

export const selectRapidDeploymentExtendedArraySpecs = createSelector(
  [
    (
      state: RootState,
      baseSuite: { tonnage: number; cost: number; power: number },
    ) => baseSuite,
  ],
  (baseSuite) => {
    return calculateRapidDeploymentExtendedArraySpecs(baseSuite);
  },
);

// Bridge system selectors
export const selectDetachableBridgeStats = createSelector(
  [
    (state: RootState, system: any) => system,
    (state: RootState) => state.shipBuilder.shipSize,
  ],
  (system, shipSize) => {
    return calculateDetachableBridgeStats(system, shipSize);
  },
);

export const selectHolographicControlsStats = createSelector(
  [
    (state: RootState, system: any) => system,
    (state: RootState) => state.shipBuilder.bridge,
    (state: RootState) => state.shipBuilder.shipSize,
  ],
  (system, bridge, shipSize) => {
    // Calculate base bridge cost for holographic controls
    const baseBridgeStats = bridge
      ? calculateBridgeStats(bridge, shipSize)
      : null;
    const baseBridgeCost =
      baseBridgeStats?.success && baseBridgeStats.data
        ? baseBridgeStats.data.cost
        : 0;
    return calculateHolographicControlsStats(system, baseBridgeCost);
  },
);

export const selectSensorStationStats = createSelector(
  [
    (state: RootState, system: any) => system,
    (state: RootState) => state.shipBuilder.shipSize,
  ],
  (system, shipSize) => {
    return calculateSensorStationStats(system, shipSize);
  },
);

// Drive system selectors
export const selectHighBurnThrusterStats = createSelector(
  [(state: RootState, thrusterThrust: number) => thrusterThrust],
  (thrusterThrust) => {
    // High-Burn Thruster: 0.5 tons per thrust
    const tonnage = thrusterThrust * 0.5;
    const cost = tonnage * 1000000; // 1M Cr per ton
    return { cost, tonnage, power: 0 };
  },
);

export const selectConcealedManeuverDriveStats = createSelector(
  [
    (state: RootState, shipTonnage: number, existingDrives: any[]) => ({
      shipTonnage,
      existingDrives,
      state,
    }),
  ],
  ({ shipTonnage, existingDrives, state }) => {
    const baseManeuverDrive = existingDrives.find((d) => d.type === "maneuver");
    if (!baseManeuverDrive) {
      return { cost: 0, tonnage: 0, power: 0 };
    }

    // Use the calculated drive stats from the Redux state
    const driveStats = state.shipBuilder.driveStats;
    const baseCost = driveStats.maneuverDriveCost;
    const baseTonnage = driveStats.maneuverDriveVolume;

    // Concealed drive adds 25% to cost and tonnage (not 2x and 1.5x as per Traveller rules)
    const cost = baseCost * 0.25; // +25% of M-drive cost
    const tonnage = baseTonnage * 0.25; // +25% of M-drive tons

    return { cost, tonnage, power: 0 };
  },
);

// Create a factory for concealed maneuver drive stats
export const createConcealedManeuverDriveStatsSelector = (
  shipTonnage: number,
  existingDrives: Drive[],
) =>
  createSelector([(state: RootState) => state], (state) => {
    const baseManeuverDrive = existingDrives.find((d) => d.type === "maneuver");
    if (!baseManeuverDrive) {
      return { cost: 0, tonnage: 0, power: 0 };
    }

    // Use the calculated drive stats from the Redux state
    const driveStats = state.shipBuilder.driveStats;
    const baseCost = driveStats.maneuverDriveCost;
    const baseTonnage = driveStats.maneuverDriveVolume;

    // Concealed drive adds 25% to cost and tonnage (not 2x and 1.5x as per Traveller rules)
    const cost = baseCost * 0.25; // +25% of M-drive cost
    const tonnage = baseTonnage * 0.25; // +25% of M-drive tons

    return { cost, tonnage, power: 0 };
  });

export const selectSolarSailStats = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => 200000, // Default costPerTon
    (state: RootState) => 0.05, // Default tonnagePercent
  ],
  (shipTonnage, costPerTon, tonnagePercent) => {
    const tonnage = shipTonnage * tonnagePercent;
    const cost = tonnage * costPerTon;
    return { cost, tonnage, power: 0 };
  },
);

// Create a factory for solar sail stats
export const createSolarSailStatsSelector = (
  shipTonnage: number,
  costPerTon: number = 200000,
  tonnagePercent: number = 0.05,
) =>
  createSelector([(state: RootState) => state], (state) => {
    const tonnage = shipTonnage * tonnagePercent;
    const cost = tonnage * costPerTon;
    return { cost, tonnage, power: 0 };
  });

// Structure system selectors
export const selectArmouredBulkheadStats = createSelector(
  [(state: RootState, protectedTonnage: number) => protectedTonnage],
  (protectedTonnage) => {
    return {
      cost: protectedTonnage * 50000, // Cr50,000 per ton
      tonnage: protectedTonnage * 0.1, // 10% of protected tonnage
      power: 0,
    };
  },
);

export const selectAdjustableHullStats = createSelector(
  [
    (state: RootState, system: any) => system,
    (state: RootState) => state.shipBuilder.shipSize,
  ],
  (system, shipSize) => {
    if (!system) {
      return { cost: 0, tonnage: 0, power: 0 };
    }
    const baseHullCost = shipSize * 50000; // Cr50,000 per ton
    if (system.id === "adjustableHull12") {
      return {
        cost: Math.round(baseHullCost * 0.1), // +10% base hull cost
        tonnage: shipSize * 0.05, // 5% of ship tonnage
        power: 0,
      };
    } else if (system.id === "adjustableHull15") {
      return {
        cost: Math.round(baseHullCost * 1.0), // +100% base hull cost
        tonnage: shipSize * 0.01, // 1% of ship tonnage
        power: 0,
      };
    }
    return { cost: 0, tonnage: 0, power: 0 };
  },
);

export const selectPressureHullStats = createSelector(
  [(state: RootState) => state.shipBuilder.shipSize],
  (shipSize) => {
    const baseHullCost = shipSize * 50000; // Cr50,000 per ton
    return {
      cost: Math.round(baseHullCost * 10.0), // 10x base hull cost
      tonnage: shipSize * 0.25, // 25% of ship tonnage
      power: 0,
    };
  },
);

// Internal system selectors
export const selectInternalArmouryStats = createSelector(
  [
    (state: RootState) => 0, // Default assignedCrew
    (state: RootState) => 0, // Default assignedMarines
  ],
  (assignedCrew, assignedMarines) => {
    const tonsCrew = Math.ceil(assignedCrew / 25);
    const tonsMarines = Math.ceil(assignedMarines / 5);
    const tons = Math.max(1, tonsCrew, tonsMarines);
    return {
      cost: tons * 250000, // Cr250,000 per ton
      tonnage: tons,
      power: 0,
    };
  },
);

// Create a factory for internal armoury stats
export const createInternalArmouryStatsSelector = (
  assignedCrew: number,
  assignedMarines: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    const tonsCrew = Math.ceil(assignedCrew / 25);
    const tonsMarines = Math.ceil(assignedMarines / 5);
    const tons = Math.max(1, tonsCrew, tonsMarines);
    return {
      cost: tons * 250000, // Cr250,000 per ton
      tonnage: tons,
      power: 0,
    };
  });

export const selectGravScreenStats = createSelector(
  [(state: RootState) => state.shipBuilder.shipSize],
  (shipSize) => {
    const tons = Math.max(1, Math.ceil(shipSize / 200)); // 1 ton per 200 tons hull (rounded up)
    return {
      cost: tons * 1000000, // Cr1,000,000 per ton
      tonnage: tons,
      power: tons * 2, // 2 power per ton
    };
  },
);

// Power system selectors
export const selectEmergencyPowerSystemStats = createSelector(
  [
    (state: RootState) => 0, // Default powerPlantCost
    (state: RootState) => 0, // Default powerPlantTonnage
  ],
  (powerPlantCost, powerPlantTonnage) => {
    return {
      cost: powerPlantCost * 0.1, // 10% of power plant cost
      tonnage: powerPlantTonnage * 0.1, // 10% of power plant tons
      power: 0, // Not a power source, just a backup
    };
  },
);

// Create a factory for emergency power system stats
export const createEmergencyPowerSystemStatsSelector = (
  powerPlantCost: number,
  powerPlantTonnage: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    return {
      cost: powerPlantCost * 0.1, // 10% of power plant cost
      tonnage: powerPlantTonnage * 0.1, // 10% of power plant tons
      power: 0, // Not a power source, just a backup
    };
  });

export const selectBatterySystemStats = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize, // This will be overridden
    (state: RootState) => 12, // Default tech level
    (state: RootState) => undefined as any, // Default system
  ],
  (batteryTonnage, batteryTechLevel, system) => {
    const costPerUnit =
      (system?.properties?.costPerUnit as Record<number, number>)?.[
        batteryTechLevel
      ] || 100000;
    const powerPerUnit =
      (system?.properties?.powerPerUnit as Record<number, number>)?.[
        batteryTechLevel
      ] || 40;
    return {
      cost: batteryTonnage * costPerUnit,
      tonnage: batteryTonnage,
      power: batteryTonnage * powerPerUnit,
    };
  },
);

// Create a factory for battery system stats
export const createBatterySystemStatsSelector = (
  batteryTonnage: number,
  batteryTechLevel: number,
  system: PowerSystem,
) =>
  createSelector([(state: RootState) => state], (state) => {
    const costPerUnit =
      (system?.properties?.costPerUnit as Record<number, number>)?.[
        batteryTechLevel
      ] || 100000;
    const powerPerUnit =
      (system?.properties?.powerPerUnit as Record<number, number>)?.[
        batteryTechLevel
      ] || 40;
    return {
      cost: batteryTonnage * costPerUnit,
      tonnage: batteryTonnage,
      power: batteryTonnage * powerPerUnit,
    };
  });

export const selectSolarPanelStats = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize, // This will be overridden
    (state: RootState) => 12, // Default tech level
    (state: RootState) => undefined as any, // Default selectedSolarPanelObj
  ],
  (solarPanelTonnage, techLevel, selectedSolarPanelObj) => {
    const costPerUnit =
      (
        selectedSolarPanelObj?.properties?.costPerUnit as Record<number, number>
      )?.[techLevel] || 100000;
    const powerPerUnit =
      (
        selectedSolarPanelObj?.properties?.powerPerUnit as Record<
          number,
          number
        >
      )?.[techLevel] || 0.25;
    const calculatedCost = solarPanelTonnage * costPerUnit;
    const calculatedTonnage = solarPanelTonnage;
    const calculatedPower = solarPanelTonnage * powerPerUnit;

    return { calculatedCost, calculatedTonnage, calculatedPower };
  },
);

// Create a factory for solar panel stats
export const createSolarPanelStatsSelector = (
  solarPanelTonnage: number,
  techLevel: number,
  selectedSolarPanelObj: PowerSystem,
) =>
  createSelector([(state: RootState) => state], (state) => {
    // Use the tech level of the selected solar panel tier
    const systemTechLevel = selectedSolarPanelObj?.techLevel || techLevel;

    const costPerUnit =
      (
        selectedSolarPanelObj?.properties?.costPerUnit as Record<number, number>
      )?.[systemTechLevel] || 100000;
    const powerPerUnit =
      (
        selectedSolarPanelObj?.properties?.powerPerUnit as Record<
          number,
          number
        >
      )?.[systemTechLevel] || 0.25;
    const calculatedCost = solarPanelTonnage * costPerUnit;
    const calculatedTonnage = solarPanelTonnage;
    const calculatedPower = solarPanelTonnage * powerPerUnit;

    return { calculatedCost, calculatedTonnage, calculatedPower };
  });

// Fuel system selectors
export const selectCollapsibleTankStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return {
      cost: inputValue * 500, // Cr500 per ton
      tonnage: inputValue,
      power: 0,
    };
  },
);

export const selectFuelProcessorStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return {
      cost: inputValue * 50000, // Cr50,000 per ton
      tonnage: inputValue,
      power: inputValue, // 1 power per ton
      processingRate: inputValue * 20, // 20 tons of fuel per day per ton of processor
    };
  },
);

export const selectFuelTankCompartmentStats = createSelector(
  [
    (state: RootState, inputValue: number) => inputValue,
  ],
  (inputValue) => {
    return {
      cost: inputValue * 4000, // Cr4,000 per ton
      tonnage: inputValue, // Tonnage equals the compartment size
      power: 0,
      detectionPenalty: {
        sensors: -4,
        investigate: -6,
      },
    };
  },
);

export const selectFuelCargoContainerStats = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize, // This will be overridden
    (state: RootState) => "fuel", // This will be overridden
  ],
  (inputValue, allocation) => {
    const additionalEquipment = inputValue * 0.05;
    const tonnage =
      allocation === "fuel"
        ? inputValue * 1.05 // full container counts against tonnage
        : inputValue * 0.05; // only equipment counts against tonnage

    return {
      cost: 0, // Cost handled elsewhere
      tonnage,
      power: 0,
      capacity: inputValue,
      allocation,
      additionalEquipment,
    };
  },
);

// Create a factory for fuel cargo container stats
export const createFuelCargoContainerStatsSelector = (
  inputValue: number,
  allocation: string,
) =>
  createSelector([(state: RootState) => state], (state) => {
    const additionalEquipment = inputValue * 0.05;
    const tonnage =
      allocation === "fuel"
        ? inputValue * 1.05 // full container counts against tonnage
        : inputValue * 0.05; // only equipment counts against tonnage

    return {
      cost: 0, // Cost handled elsewhere
      tonnage,
      power: 0,
      capacity: inputValue,
      allocation,
      additionalEquipment,
    };
  });

export const selectMountableTankStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return {
      cost: inputValue * 1000, // Cr1,000 per ton
      tonnage: inputValue,
      power: 0,
    };
  },
);

export const selectDropTankStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return {
      cost: inputValue * 2000, // Cr2,000 per ton
      tonnage: inputValue,
      power: 0,
    };
  },
);

export const selectRamscoopStats = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState) => state.shipBuilder.shipSize, // This will be overridden by the component
  ],
  (shipTonnage, additionalTonnage) => {
    const baseTonnage = Math.max(10, Math.ceil(shipTonnage * 0.01 + 5));
    const totalTonnage = baseTonnage + additionalTonnage;
    const cost = totalTonnage * 250000; // Cr250,000 per ton
    const collectionRate = totalTonnage * 5; // tons per week

    return {
      cost,
      tonnage: totalTonnage,
      power: 0,
      collectionRate,
    };
  },
);

// Create a factory for ramscoop stats that accepts parameters
export const createRamscoopStatsSelector = (
  shipTonnage: number,
  additionalTonnage: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    const baseTonnage = Math.max(10, Math.ceil(shipTonnage * 0.01 + 5));
    const totalTonnage = baseTonnage + additionalTonnage;
    const cost = totalTonnage * 250000; // Cr250,000 per ton
    const collectionRate = totalTonnage * 5; // tons per week

    return {
      cost,
      tonnage: totalTonnage,
      power: 0,
      collectionRate,
    };
  });

// Drone system selectors
export const selectMiningDroneStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return calculateMiningDroneSpecs(inputValue);
  },
);

export const selectProbeDroneStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return calculateProbeDroneSpecs(inputValue);
  },
);

export const selectAdvancedProbeDroneStats = createSelector(
  [(state: RootState, inputValue: number) => inputValue],
  (inputValue) => {
    return calculateAdvancedProbeDroneSpecs(inputValue);
  },
);

export const selectRepairDroneStats = createSelector(
  [(state: RootState, shipTonnage: number) => shipTonnage],
  (shipTonnage) => {
    return calculateRepairDroneSpecs(shipTonnage);
  },
);

// Common area system selectors
export const selectAdvancedEntertainmentStats = createSelector(
  [
    (state: RootState) => 0, // Default techLevel
    (state: RootState) => 0, // Default cost
  ],
  (techLevel, cost) => {
    return {
      cost,
      tonnage: 0,
      power: 0,
      techLevel,
    };
  },
);

// Create a factory for advanced entertainment stats
export const createAdvancedEntertainmentStatsSelector = (
  techLevel: number,
  cost: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    return {
      cost,
      tonnage: 0,
      power: 0,
      techLevel,
    };
  });

export const selectBreweryStats = createSelector(
  [
    (state: RootState) => 0, // Default tonnage
    (state: RootState) => 0, // Default costPerTon
  ],
  (tonnage, costPerTon) => {
    return {
      cost: tonnage * costPerTon,
      tonnage,
      power: 0,
    };
  },
);

// Create a factory for brewery stats
export const createBreweryStatsSelector = (
  tonnage: number,
  costPerTon: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    return {
      cost: tonnage * costPerTon,
      tonnage,
      power: 0,
    };
  });

export const selectGourmetKitchenStats = createSelector(
  [
    (state: RootState) => 0, // Default diners
    (state: RootState) => 0, // Default costPerDiner
  ],
  (diners, costPerDiner) => {
    return {
      cost: diners * costPerDiner,
      tonnage: diners,
      power: 0,
      diners,
    };
  },
);

// Create a factory for gourmet kitchen stats
export const createGourmetKitchenStatsSelector = (
  diners: number,
  costPerDiner: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    return {
      cost: diners * costPerDiner,
      tonnage: diners,
      power: 0,
      diners,
    };
  });

export const selectHotTubStats = createSelector(
  [
    (state: RootState) => 0, // Default users
    (state: RootState) => 0, // Default tonsPerTraveller
    (state: RootState) => 0, // Default costPerTon
  ],
  (users, tonsPerTraveller, costPerTon) => {
    const tonnage = users * tonsPerTraveller;
    return {
      cost: tonnage * costPerTon,
      tonnage,
      power: 0,
      travellers: users,
    };
  },
);

// Create a factory for hot tub stats
export const createHotTubStatsSelector = (
  users: number,
  tonsPerTraveller: number,
  costPerTon: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    const tonnage = users * tonsPerTraveller;
    return {
      cost: tonnage * costPerTon,
      tonnage,
      power: 0,
      travellers: users,
    };
  });

export const selectSwimmingPoolStats = createSelector(
  [
    (state: RootState) => "standard" as const, // Default poolType
    (state: RootState) => 0, // Default tonnage
    (state: RootState) => 0, // Default costPerTon
    (state: RootState) => 0, // Default techLevel
  ],
  (poolType, tonnage, costPerTon, techLevel) => {
    return {
      cost: tonnage * costPerTon,
      tonnage: poolType === "standard" ? tonnage : 0, // endless pool has fixed tonnage
      power: 0,
      techLevel,
      poolType,
    };
  },
);

// Create a factory for swimming pool stats
export const createSwimmingPoolStatsSelector = (
  poolType: "standard" | "endless",
  tonnage: number,
  costPerTon: number,
  techLevel: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    return {
      cost: tonnage * costPerTon,
      tonnage: poolType === "standard" ? tonnage : 0, // endless pool has fixed tonnage
      power: 0,
      techLevel,
      poolType,
    };
  });

export const selectTheatreStats = createSelector(
  [
    (state: RootState) => "theatreBasic" as const, // Default theatreType
    (state: RootState) => 0, // Default tonnage
    (state: RootState) => 0, // Default costPerTon
    (state: RootState) => 0, // Default techLevel
  ],
  (theatreType, tonnage, costPerTon, techLevel) => {
    return {
      cost: tonnage * costPerTon,
      tonnage,
      power: 0,
      techLevel,
    };
  },
);

// Create a factory for theatre stats
export const createTheatreStatsSelector = (
  theatreType: "theatreBasic" | "theatreAdvanced",
  tonnage: number,
  costPerTon: number,
  techLevel: number,
) =>
  createSelector([(state: RootState) => state], (state) => {
    return {
      cost: tonnage * costPerTon,
      tonnage,
      power: 0,
      techLevel,
    };
  });

export const selectZeroGRoomStats = createSelector(
  [(state: RootState, tonnage: number) => tonnage],
  (tonnage) => {
    return {
      cost: tonnage * 50000, // Cr50,000 per ton
      tonnage,
      power: 0,
    };
  },
);

// Accommodation system selectors
export const selectBarracksStats = createSelector(
  [(state: RootState, passengers: number) => passengers],
  (passengers) => {
    return {
      cost: passengers * 50000, // Cr50,000 per passenger
      tonnage: passengers * 1, // 1 ton per passenger (as per specification)
      power: 0,
      passengers,
    };
  },
);

export const selectBrigStats = createSelector(
  [(state: RootState, quantity: number) => quantity],
  (quantity) => {
    return {
      cost: quantity * 100000, // Cr100,000 per cell
      tonnage: quantity * 1, // 1 ton per cell
      power: 0,
      quantity,
    };
  },
);

export const selectCabinSpaceStats = createSelector(
  [(state: RootState, passengers: number) => passengers],
  (passengers) => {
    return {
      cost: passengers * 25000, // Cr25,000 per passenger
      tonnage: passengers * 0.25, // 0.25 tons per passenger
      power: 0,
      passengers,
    };
  },
);

export const selectMultiEnvironmentSpaceStats = createSelector(
  [(state: RootState, tonnage: number) => tonnage],
  (tonnage) => {
    return {
      cost: tonnage * 100000, // Cr100,000 per ton
      tonnage,
      power: tonnage, // 1 power per ton
    };
  },
);

// Small craft selectors
export const selectTotalExternalSmallCraftTonnage = createSelector(
  [(state: RootState) => state.shipBuilder.smallCraft],
  (smallCraft) => {
    if (!smallCraft || !Array.isArray(smallCraft)) {
      return 0;
    }
    return smallCraft
      .filter(craft => craft.dockingLocation === 'external')
      .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
  },
);

export const selectTotalInternalSmallCraftTonnage = createSelector(
  [(state: RootState) => state.shipBuilder.smallCraft],
  (smallCraft) => {
    if (!smallCraft || !Array.isArray(smallCraft)) {
      return 0;
    }
    return smallCraft
      .filter(craft => craft.dockingLocation === 'internal')
      .reduce((total, craft) => total + (craft.size * craft.quantity), 0);
  },
);

export const selectSmallCraft = createSelector(
  [(state: RootState) => state.shipBuilder.smallCraft],
  (smallCraft) => smallCraft || [],
);

// External system selectors
export const selectDockingClampStats = createSelector(
  [(state: RootState, clampType: string) => clampType],
  (clampType) => {
    const clampTypes = {
      typeI: { cost: 500000, tonnage: 1, maxTonnage: 30 },
      typeII: { cost: 1000000, tonnage: 5, maxTonnage: 99 },
      typeIII: { cost: 2000000, tonnage: 10, maxTonnage: 300 },
      typeIV: { cost: 4000000, tonnage: 20, maxTonnage: 2000 },
      typeV: { cost: 8000000, tonnage: 50, maxTonnage: 999999 },
    };

    const selected =
      clampTypes[clampType as keyof typeof clampTypes] || clampTypes.typeI;
    return {
      cost: selected.cost,
      tonnage: selected.tonnage,
      power: 0,
      maxAttachedTonnage: selected.maxTonnage,
    };
  },
);

export const selectForcedLinkageStats = createSelector(
  [(state: RootState, linkageType: string) => linkageType],
  (linkageType) => {
    const linkageTypes = {
      basic: { cost: 50000, pilotDM: -2, techLevel: 7 },
      improved: { cost: 75000, pilotDM: -1, techLevel: 9 },
      enhanced: { cost: 100000, pilotDM: 0, techLevel: 12 },
      advanced: { cost: 500000, pilotDM: 2, techLevel: 15 },
    };

    const selected =
      linkageTypes[linkageType as keyof typeof linkageTypes] ||
      linkageTypes.basic;
    return {
      cost: selected.cost,
      tonnage: 2,
      power: 0,
      pilotDM: selected.pilotDM,
      techLevel: selected.techLevel,
    };
  },
);

export const selectGrapplingArmStats = createSelector(
  [(state: RootState, armType: string) => armType],
  (armType) => {
    const armTypes = {
      standard: { cost: 1000000, tonnage: 2, maxWeight: 2 },
      heavy: { cost: 3000000, tonnage: 6, maxWeight: 10 },
    };

    const selected =
      armTypes[armType as keyof typeof armTypes] || armTypes.standard;
    return {
      cost: selected.cost,
      tonnage: selected.tonnage,
      power: 0,
      maxWeight: selected.maxWeight,
    };
  },
);

// Multi-Environment Space Inline Editor selector
export const selectMESInlineEditorStats = createSelector(
  [(state: RootState, coveredSpaces: Array<{ tons: number }>) => coveredSpaces],
  (coveredSpaces) => {
    const totalTons = coveredSpaces.reduce((sum, s) => sum + s.tons, 0);
    const equipmentTonnage = Math.round(totalTons * 0.05 * 100) / 100;
    const totalTonnage = Math.round(totalTons * 1.05 * 100) / 100;
    const cost = totalTons * 500000; // Cr500,000 per ton
    const power = totalTons; // 1 power per ton

    return {
      cost,
      tonnage: totalTonnage,
      power,
      equipmentTonnage,
      totalTons,
    };
  },
);

// Modular hull system selectors
export const selectModularHullExcludedTonnage = createSelector(
  [
    (state: RootState) => state.shipBuilder.bridge,
    (state: RootState) => state.shipBuilder.powerPlant,
    (state: RootState) => state.shipBuilder.drives,
    (state: RootState) => state.optionalSystems.structure,
    (state: RootState) => state.shipBuilder.armor,
    (state: RootState) => state.shipBuilder.shipSize,
    (state: RootState, shipTonnage: number) => shipTonnage,
  ],
  (
    bridge,
    powerPlant,
    drives,
    structureSystems,
    armor,
    shipSize,
    shipTonnage,
  ) => {
    const bridgeTons = bridge?.weight || 0;
    const powerPlantTons = powerPlant?.weight || 0;
    const drivesTons = drives.reduce((sum, d) => {
      return sum + getDriveTons(d.type, d.rating, shipSize);
    }, 0);
    const structureTons = structureSystems.reduce(
      (sum, s) => sum + (s.tonnage || 0),
      0,
    );
    const armorTons = armor?.weight || 0;
    return bridgeTons + powerPlantTons + drivesTons + structureTons + armorTons;
  },
);

export const selectMaxModularTonnage = createSelector(
  [
    (state: RootState, shipTonnage: number) => shipTonnage,
    (state: RootState, shipTonnage: number) =>
      selectModularHullExcludedTonnage(state, shipTonnage),
  ],
  (shipTonnage, excludedTonnage) => {
    const MAX_MODULAR_PERCENT = 75;
    const baseForModular = Math.max(0, shipTonnage - excludedTonnage);
    return Math.round((baseForModular * MAX_MODULAR_PERCENT) / 100);
  },
);

export const selectModularHullCost = createSelector(
  [
    (state: RootState, shipTonnage: number) => shipTonnage,
    (state: RootState, shipTonnage: number, modularPercent: number) =>
      modularPercent,
  ],
  (shipTonnage, modularPercent) => {
    const baseHullCost = shipTonnage * 50000;
    return baseHullCost * (1 + modularPercent / 100);
  },
);

export const selectModularTonnage = createSelector(
  [
    (state: RootState, shipTonnage: number) => shipTonnage,
    (state: RootState, shipTonnage: number, modularPercent: number) =>
      modularPercent,
    (state: RootState, shipTonnage: number, modularPercent: number) =>
      selectModularHullExcludedTonnage(state, shipTonnage),
  ],
  (shipTonnage, modularPercent, excludedTonnage) => {
    const baseForModular = Math.max(0, shipTonnage - excludedTonnage);
    return Math.round((baseForModular * modularPercent) / 100);
  },
);

// Generic system selectors
export const selectStructureSystemStats = createSelector(
  [
    (state: RootState, system: any, shipTonnage: number) => ({
      system,
      shipTonnage,
    }),
  ],
  ({ system, shipTonnage }) => {
    if (system.subtype === "adjustableHull") {
      const tonnage =
        system.id === "adjustableHull12"
          ? shipTonnage * 0.05
          : shipTonnage * 0.01;
      const cost =
        system.id === "adjustableHull12"
          ? Math.round(shipTonnage * 50000 * 0.1)
          : Math.round(shipTonnage * 50000 * 1.0);
      return { cost, tonnage, power: 0 };
    } else if (system.subtype === "pressureHull") {
      const tonnage = shipTonnage * 0.25;
      const cost = Math.round(shipTonnage * 50000 * 10);
      return { cost, tonnage, power: 0 };
    }
    return { cost: 0, tonnage: 0, power: 0 };
  },
);

// Move these up before the selectors object
export const selectMaintenanceCost = createSelector(
  [
    (state: RootState) => selectMortgage(state),
    (state: RootState) => selectLifeSupportCost(state),
    (state: RootState) => shipBuilderSelectors.selectCrewSalaryTotal(state),
  ],
  (mortgage, lifeSupport, salaries) => {
    return (mortgage || 0) + (lifeSupport || 0) + (salaries || 0);
  },
);
export const selectTotalPowerAvailable = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectPowerEfficiency = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectTotalCrewCost = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectTotalMonthlyCosts = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectCargoEfficiency = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectJumpEfficiency = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectTotalOperatingCosts = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);
export const selectTotalAnnualCosts = createSelector(
  [(state: RootState) => 0],
  (val) => 0,
);



// When selecting or updating crewComposition, ensure all roles are present with default 0
// Example selector (add or update as needed):
export const selectFullCrewComposition = (state: RootState) => {
  const crew = state.shipBuilder.crewComposition || {};
  return {
    captain: crew.captain ?? 0,
    pilot: crew.pilot ?? 0,
    astrogator: crew.astrogator ?? 0,
    engineer: crew.engineer ?? 0,
    maintenance: crew.maintenance ?? 0,
    gunner: crew.gunner ?? 0,
    steward: crew.steward ?? 0,
    administrator: crew.administrator ?? 0,
    sensorOperator: crew.sensorOperator ?? 0,
    medic: crew.medic ?? 0,
    officer: crew.officer ?? 0,
    marines: crew.marines ?? 0,
  };
};

// Selector for total used cargo/weight including supplies and ammunition
export const selectTotalUsedCargoTonnage = createSelector(
  [
    (state) => state.shipBuilder.supplyTons,
    (state) => state.shipBuilder.ammunitionStorage || [],
    (state) => state.optionalSystems,
    (state) => state.shipBuilder.weaponMounts || [],
    (state) => state.shipBuilder.defensiveSystems || [],
    (state) => state.shipBuilder.stateroomConfig,
    (state) => state.shipBuilder.fuelTanks,
    (state) => state.shipBuilder.hullAndArmorStats,
    (state) => state.shipBuilder.hull,
    (state) => state.shipBuilder.armor,
    (state) => state.shipBuilder.bridge,
    (state) => state.shipBuilder.sensors,
    (state) => state.shipBuilder.drives || [],
    (state) => state.shipBuilder.driveStats,
    (state) => state.shipBuilder.powerPlant,
    (state) => state.shipBuilder.smallCraft || [],
  ],
  (supplyTons, ammunitionStorage, optionalSystems, weaponMounts, defensiveSystems, stateroomConfig, fuelTanks, hullAndArmorStats, hull, armor, bridge, sensors, drives, driveStats, powerPlant, smallCraft) => {
    // Sum all optional systems tonnage
    const systemsTonnage = [
      ...optionalSystems.structure,
      ...optionalSystems.modularHull,
      ...optionalSystems.power,
      ...optionalSystems.drives,
      ...optionalSystems.fuel,
      ...optionalSystems.accommodations,
      ...optionalSystems.bridge,
      ...optionalSystems.cargo.filter((system: any) => system.subtype !== "externalCargoMount"),
      ...optionalSystems.drones,
      ...optionalSystems.sensors,
      ...optionalSystems.external,
      ...optionalSystems.internal,
      ...optionalSystems.software,
      ...optionalSystems.commonAreas,
    ].reduce((total: number, system: any) => total + (system.tonnage || 0), 0);

    // Calculate weapon mounts tonnage (use mount.tons which includes mount base + weapon weight)
    const weaponMountsTonnage = weaponMounts.reduce((total: number, mount: any) => {
      return total + (mount.tons || 0);
    }, 0);

    // Calculate defensive systems tonnage (multiply by quantity per Traveller rules)
    const defensiveSystemsTonnage = defensiveSystems.reduce((total: number, system: any) => {
      const quantity = system.quantity || 1;
      return total + ((system.weight || 0) * quantity);
    }, 0);

    // Calculate stateroom tonnage (match footer calculation including common area)
    let stateroomTonnage = 0;
    if (stateroomConfig) {
      stateroomTonnage += (stateroomConfig.single || 0) * 4; // Single staterooms (4 tons each)
      stateroomTonnage += (stateroomConfig.double || 0) * 4; // Double staterooms (4 tons each, same as single)
      if (stateroomConfig.lowBerths) {
        stateroomTonnage += (stateroomConfig.lowBerths.count || 0) * 0.5; // Low berths (0.5 tons each)
      }
      if (stateroomConfig.emergencyLowBerths) {
        stateroomTonnage += (stateroomConfig.emergencyLowBerths.count || 0) * 1; // Emergency low berths (1 ton each)
      }
      // Add common area tonnage (this was missing!)
      stateroomTonnage += (stateroomConfig.commonArea || 0);
    }

    // Calculate fuel tanks tonnage (main fuel tankage from Step 4 - this DOES consume internal space)
    const fuelTanksTonnage = fuelTanks || 0;

    // Calculate ammunition storage tonnage
    const ammoTons = ammunitionStorage.reduce((total: number, ammo: any) => {
      const quantity = ammo.quantity || 0;
      return total + ((ammo.tonsPerReload || 0) * quantity);
    }, 0);

    // Calculate hull and armor tonnage using consumed volume (not base weight)
    // This avoids double-counting with optional systems
    const hullAndArmorTonnage = hullAndArmorStats?.totalConsumedVolume || 0;

    // Calculate bridge tonnage
    const bridgeTonnage = bridge?.weight || 0;

    // Calculate sensors tonnage
    const sensorsTonnage = sensors?.tons || 0;

    // Calculate drives tonnage using pre-calculated driveStats
    const drivesTonnage = driveStats?.totalConsumedVolume || 0;

    // Calculate power plant tonnage
    const powerPlantTonnage = powerPlant?.weight || 0;

    // Calculate small craft tonnage (ONLY external small craft weights affect ship size)
    // Internal small craft weights are already accounted for by their docking space tonnage
    const smallCraftTonnage = smallCraft.reduce((total: number, craft: any) => {
      // Only add weight for external small craft (docking clamps)
      // Internal small craft (hangars, docking spaces) don't add to ship size
      if (craft.dockingLocation === 'external') {
        return total + (craft.size * craft.quantity);
      }
      return total;
    }, 0);

    // Sum all tonnage (INCLUDING fuel tanks - they DO consume internal space)
    return systemsTonnage + 
           weaponMountsTonnage + 
           defensiveSystemsTonnage + 
           stateroomTonnage + 
           ammoTons + 
           (supplyTons || 0) + 
           fuelTanksTonnage + 
           hullAndArmorTonnage + 
           bridgeTonnage + 
           sensorsTonnage + 
           drivesTonnage + 
           powerPlantTonnage + 
           smallCraftTonnage;
  }
);

// Available cargo space selector - derives from total used cargo tonnage
export const selectAvailableCargoSpace = createSelector(
  [
    (state: RootState) => state.shipBuilder.shipSize,
    selectTotalUsedCargoTonnage,
  ],
  (shipSize, totalUsedCargoTonnage) => {
    return Math.max(0, shipSize - totalUsedCargoTonnage);
  },
);

export const selectCargoCraneStats = createSelector(
  [selectAvailableCargoSpace],
  (availableCargoSpace) => {
    // Use the actual calculation function from utils
    const tonnage = 2.5 + Math.ceil(availableCargoSpace / 150) * 0.5; // 2.5 + 0.5 per 150 tons (or part thereof)
    const cost = tonnage * 1000000; // MCr1 per ton of crane

    return {
      cost,
      tonnage,
      power: 0,
    };
  },
);



